import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import axios from 'axios';
import { motion } from 'framer-motion';
import { getCountdown } from '../helper/Countdown';

const HotDealsOne = () => {
  const [timeLeft, setTimeLeft] = useState(getCountdown());
  const [hotDealsProducts, setHotDealsProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getCountdown());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/hot-deals/`);
        setHotDealsProducts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching hot deals products:', error);
        setLoading(false);
      }
    };
    fetchData();

    // HIGHLIGHT PRODUCT IMAGES - BORDERLESS CARDS
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      .hot-deals .product-card {
        border: 2px solid #299E60 !important;
        background: #f9fcfb !important;
        padding: 10px !important;
        border-radius: 24px !important;
        display: flex !important;
        flex-direction: column !important;
        height: auto !important;
        transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
        box-shadow: 0 8px 25px rgba(41, 158, 96, 0.12) !important;
        overflow: hidden !important;
      }
      .hot-deals .product-card:hover {
        transform: translateY(-5px) !important;
        box-shadow: 0 12px 35px rgba(41, 158, 96, 0.2) !important;
        background: #ffffff !important;
        border-color: #35c677 !important;
      }
      .hot-deals .product-card__thumb {
        width: 100% !important;
        height: 180px !important;
        border-radius: 16px !important;
        background: #fff !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin-bottom: 12px !important;
        overflow: hidden !important;
        border: none !important;
      }
      .hot-deals .product-card__thumb img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
      }
      .hot-deals .product-card:hover .product-card__thumb img {
        transform: scale(1.1);
      }
      .hot-deals .product-card__content {
        text-align: center !important;
        padding: 10px 5px !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
      }
      .hot-deals .product-card__cart {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 10px auto 0 !important;
        width: fit-content !important;
        min-width: 140px !important;
        background: linear-gradient(90deg, #25a163 0%, #30c075 100%) !important;
        color: #fff !important;
        font-weight: 700 !important;
        letter-spacing: 0.5px !important;
        border-radius: 12px !important;
        padding: 12px 24px !important;
        box-shadow: 0 4px 12px rgba(37, 161, 99, 0.2) !important;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
        text-decoration: none !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
      }
      .hot-deals .product-card__cart:hover {
        background: linear-gradient(90deg, #1e8551 0%, #25a163 100%) !important;
        color: #fff !important;
        transform: scale(1.05) translateY(-2px) !important;
        box-shadow: 0 6px 15px rgba(37, 161, 99, 0.3) !important;
      }
      .hot-deals .product-card__content h6.title {
        font-weight: 800 !important;
        font-size: 1.15rem !important;
        margin-bottom: 12px !important;
        color: #000000 !important;
      }
      .hot-deals .product-card__content h6.title .link {
        color: #000000 !important;
        text-decoration: none !important;
      }
      .hot-deals .product-card__content h6.title .link:hover {
        color: #299E60 !important;
      }
      .hot-deals .product-card__price {
        font-weight: 700 !important;
        color: #299E60 !important;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 800,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      { breakpoint: 1399, settings: { slidesToShow: 3 } },
      { breakpoint: 1199, settings: { slidesToShow: 2 } },
      { breakpoint: 575, settings: { slidesToShow: 1 } },
    ]
  };

  const getBadgeClass = (badgeColor) => {
    const colorMap = {
      info: 'bg-info-600',
      danger: 'bg-danger-600',
      warning: 'bg-warning-600',
      success: 'bg-success-600',
      primary: 'bg-primary-600'
    };
    return colorMap[badgeColor] || 'bg-primary-600';
  };

  const goToPrevious = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  if (loading) {
    return (
      <section className="hot-deals">
        <div className="container container-lg">
          <div className="text-center py-5">
            <div className="spinner-border text-main-600" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (hotDealsProducts.length === 0) return null;

  return (
    <section className="hot-deals pt-80 pb-20">
      <div className="container container-lg">
        {/* Section Heading */}
        <div className="section-heading mb-24">
          <div className="flex-between flex-wrap gap-8">
            <h5 className="mb-0">Hot Deals</h5>
            <div className="flex-align gap-16">
              <div className="flex-align gap-8">
                <button
                  onClick={goToPrevious}
                  className="w-40 h-40 flex-center rounded-circle transition-1"
                  aria-label="Previous"
                >
                  <i className="ph ph-caret-left text-xl" />
                </button>
                <button
                  onClick={goToNext}
                  className="w-40 h-40 flex-center rounded-circle transition-1"
                  aria-label="Next"
                >
                  <i className="ph ph-caret-right text-xl" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FLEX CONTAINER */}
        <motion.div
          className="hot-deals-container"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.15
              }
            }
          }}
        >
          {/* Featured Card */}
          <motion.div
            className="hot-deals-featured hot-deal-responsive"
            variants={{
              hidden: { opacity: 0, x: -30 },
              show: { opacity: 1, x: 0 }
            }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <div className="hot-deals-card">
              <img
                src="assets/images/shape/offer-shape.png"
                alt=""
                className="hot-deals-card__bg"
              />
              <div className="hot-deals-card__thumb">
                <img src="assets/images/thumbs/Product15.jpg" alt="Hot Deals" />
              </div>
              <div className="hot-deals-card__content">
                <h4 className="hot-deals-card__title">Limited Time Offers</h4>
                <div className="hot-deals-countdown">
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.hours}</span>
                    <span className="countdown-label">Hours</span>
                  </div>
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.minutes}</span>
                    <span className="countdown-label">Mins</span>
                  </div>
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.seconds}</span>
                    <span className="countdown-label">Secs</span>
                  </div>
                </div>
                <Link to="/shop" className="hot-deals-card__btn">
                  Explore Now <i className="ph ph-arrow-right" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Products Slider */}
          <motion.div
            className="hot-deals-products"
            variants={{
              hidden: { opacity: 0, x: 30 },
              show: { opacity: 1, x: 0 }
            }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <Slider ref={sliderRef} {...settings}>
              {hotDealsProducts.map((product) => {
                const materialData = product.material_data || {};
                const originalPrice = parseFloat(materialData.material_cost_per_sqft || 0);
                const salePercentage = product.sale_percentage || 0;
                const discountedPrice = salePercentage > 0
                  ? (originalPrice * (1 - salePercentage / 100)).toFixed(2)
                  : originalPrice.toFixed(2);

                return (
                  <div key={product.id} className="px-8">
                    <div className="product-card h-100 p-8 position-relative transition-2">
                      {salePercentage > 0 && (
                        <span className="product-card__badge bg-danger-600 px-8 py-4 text-sm text-white">
                          Sale {salePercentage}%
                        </span>
                      )}

                      <Link
                        to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                        className="product-card__thumb flex-center"
                      >
                        <img
                          src={materialData.swatch_url || 'assets/images/thumbs/default-product.jpg'}
                          alt={materialData.name || product.title}
                          onError={(e) => e.target.src = 'assets/images/thumbs/default-product.jpg'}
                        />
                      </Link>

                      <div className="product-card__content p-sm-2 text-center">
                        <h6 className="title text-lg fw-semibold mb-8">
                          <Link
                            to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                            className="link text-line-2"
                          >
                            {materialData.name || product.title}
                          </Link>
                        </h6>

                        <div className="product-card__price mb-12">
                          {salePercentage > 0 ? (
                            <div className="d-flex align-items-center gap-8 flex-wrap justify-content-center">
                              <span className="text-heading text-md fw-semibold">
                                ${discountedPrice}{" "}
                                <span className="text-gray-500 fw-normal">/sqft</span>
                              </span>
                              <span className="text-gray-400 text-sm fw-semibold text-decoration-line-through">
                                ${originalPrice.toFixed(2)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-heading text-md fw-semibold">
                              ${originalPrice.toFixed(2)}{" "}
                              <span className="text-gray-500 fw-normal">/sqft</span>
                            </span>
                          )}
                        </div>

                        <div className="flex-align gap-6 mb-16 justify-content-center">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                          ))}
                          <span className="text-13 text-gray-500 fw-medium ms-4">(4.8)</span>
                        </div>

                        <Link
                          to={`/product-details-material?type=${materialData.product_type}&material=${product.material}`}
                          className="product-card__cart flex-align gap-8 w-100 justify-content-center"
                        >
                          Customize <i className="ph ph-magic-wand" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HotDealsOne;
